import { createElement, ReactNode } from 'react';

import { createInertiaApp, router } from '@inertiajs/react';
import { createRoot } from 'react-dom/client';

import { CookieConsentBanner } from '../components/shared/CookieConsentBanner';
import Auth from '../layouts/Auth';
import Checkout from '../layouts/Checkout';
import Layout from '../layouts/Layout';
import Profile from '../layouts/Profile';
import Users from '../layouts/Users';
import { CookieConsentService } from '../services/CookieConsentService';
import { updateCsrfToken } from '../services/CsrfTokenService';
import { ThemeService } from '../services/ThemeService';

// Temporary type definition, until @inertiajs/react provides one
type ResolvedComponent = {
    default: {
        layout: (page: ReactNode) => ReactNode;
    };
};

createInertiaApp({
    resolve: (name) => {
        const pages = import.meta.glob<ResolvedComponent>('../pages/**/*.tsx', {
            eager: true,
        });
        const page = pages[`../pages/${name}.tsx`];
        if (!page) {
            console.error(`Missing Inertia page component: '${name}.tsx'`);
        }

        if (name.startsWith('Auth/')) {
            page.default.layout ??= (page: ReactNode) => <Auth>{page}</Auth>;
        } else if (name.startsWith('Profiles/')) {
            page.default.layout ??= (page: ReactNode) => (
                <Profile>{page}</Profile>
            );
        } else if (name.startsWith('Orders/')) {
            page.default.layout ??= (page: ReactNode) => (
                <Checkout>{page}</Checkout>
            );
        } else if (name.startsWith('Users/')) {
            page.default.layout ??= (page: ReactNode) => <Users>{page}</Users>;
        } else {
            page.default.layout ??= (page: ReactNode) => (
                <Layout>{page}</Layout>
            );
        }

        return page;
    },

    setup({ el, App, props }) {
        if (el) {
            ThemeService.syncTheme();
            CookieConsentService.applyStoredConsent();
            updateCsrfToken(props.initialPage.props.csrfToken);
            router.on('navigate', (event) => {
                updateCsrfToken(event.detail.page.props.csrfToken);
            });
            createRoot(el).render(
                <>
                    {createElement(App, props)}
                    <CookieConsentBanner />
                </>,
            );
        } else {
            console.error(
                'Missing root element.\n\n' +
                    'If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n' +
                    'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
            );
        }
    },
});
